import React, { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import RootLayout from '../layouts/RootLayout';
import AppLayout from '../layouts/AppLayout';

import ExternalRedirect from '../components/ExternalRedirect';
import { getRedirectUrl } from '../utils/helpers';

const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const SocialsPage = lazy(() => import('../pages/SocialsPage'));
const GalleryPage = lazy(() => import('../pages/GalleryPage'));
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const TimeSlotsPage = lazy(() => import('../pages/TimeSlotsPage'));
const OnboardingPage = lazy(() => import('../pages/OnboardingPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const ErrorPage = lazy(() => import('../pages/ErrorPage'));
const TestPage = lazy(() => import('../pages/TestPage'));
const HtmlEditorPage = lazy(() => import('../pages/HtmlEditorPage'));
const CampaignAgentPage = lazy(() => import('../pages/CampaignAgentPage'));
const CampaignsPage = lazy(() => import('../pages/CampaignsPage'));
const CampaignDetailPage = lazy(() => import('../pages/CampaignDetailPage'));
const BrandPage = lazy(() => import('../pages/BrandPage'));
const PostPage = lazy(() => import('../pages/PostPage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));

const RouteFallback = () => (
  <div className='flex min-h-[40vh] items-center justify-center px-4'>
    <div
      className='rounded-xl px-4 py-3 text-sm font-medium'
      style={{
        background: 'var(--bg-card)',
        color: 'var(--text-muted)',
        border: '1px solid var(--border)',
      }}
    >
      Loading...
    </div>
  </div>
);

const withSuspense = (Component) => (
  <Suspense fallback={<RouteFallback />}>
    <Component />
  </Suspense>
);

const redirectUriForSettings = getRedirectUrl('/dashboard/settings');

export const routes = [
  {
    path: '/test',
    element: withSuspense(TestPage),
  },
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <Navigate to='/dashboard' replace />,
      },
      {
        path: 'onboarding',
        element: withSuspense(OnboardingPage),
      },
      {
        path: '/',
        element: <AppLayout />,
        children: [
          {
            path: 'dashboard',
            element: withSuspense(DashboardPage),
          },
          {
            path: 'socials',
            element: withSuspense(SocialsPage),
          },
          {
            path: 'gallery',
            element: withSuspense(GalleryPage),
          },
          {
            path: 'profile',
            element: withSuspense(ProfilePage),
          },
          {
            path: 'schedule',
            element: withSuspense(TimeSlotsPage),
          },
          {
            path: 'settings',
            element: <ExternalRedirect url={redirectUriForSettings} />,
          },
          {
            path: 'html-editor',
            element: withSuspense(HtmlEditorPage),
          },
          {
            path: 'create',
            element: withSuspense(CampaignAgentPage),
          },
          {
            path: 'campaigns',
            element: withSuspense(CampaignsPage),
          },
          {
            path: 'campaigns/:id',
            element: withSuspense(CampaignDetailPage),
          },
          {
            path: 'brand',
            element: withSuspense(BrandPage),
          },
          {
            path: 'posts/:id',
            element: withSuspense(PostPage),
          },
          {
            path: 'notifications',
            element: withSuspense(NotificationsPage),
          },
        ],
      },
      {
        path: 'error',
        element: withSuspense(ErrorPage),
      },
      {
        path: '*',
        element: withSuspense(NotFoundPage),
      },
    ],
  },
];
